import { useState } from "react";
import { toast } from "sonner";
import { STATUS_OPTIONS, STATUS_LABELS, STATUS_TONE, shortDate } from "../../lib/status";
import { updateBookingStatus, updateBookingDetails } from "../../lib/api";

// Status pill (select) + inline date / amount, notebook style: "BOOK 17/12", "HECHO 15/8 $360".
export function StatusCell({ booking: b, artistId, onChanged }) {
  const [pending, setPending] = useState(null); // "scheduled" | "archived_done"
  const [draft, setDraft] = useState("");

  const patch = async (payload) => {
    try {
      const updated = await updateBookingDetails(b.id, payload);
      onChanged(updated);
    } catch {
      toast.error("Could not save");
    }
  };

  const setStatus = async (status, extra = {}) => {
    try {
      const updated = await updateBookingStatus(b.id, status, extra);
      onChanged(updated);
      setPending(null);
      toast.success(`${b.name} → ${STATUS_LABELS[status]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  const onSelect = (s) => {
    if (s === b.status) return;
    if (s === "scheduled") { setPending("scheduled"); setDraft(b.scheduled_date || ""); return; }
    if (s === "archived_done") { setPending("archived_done"); setDraft(b.amount_charged ? String(b.amount_charged) : ""); return; }
    setStatus(s);
  };

  const confirmPending = () => {
    if (pending === "scheduled") {
      if (!draft) { toast.error("Pick a date"); return; }
      setStatus("scheduled", { scheduled_date: draft, scheduled_artist: b.scheduled_artist || artistId || b.preferred_artist });
    } else if (pending === "archived_done") {
      const n = parseFloat(draft);
      if (isNaN(n) || n < 0) { toast.error("Enter a valid amount"); return; }
      setStatus("archived_done", { amount_charged: n });
    }
  };

  const status = b.status === "archived" ? "archived_done" : b.status;
  const tone = STATUS_TONE[status] || STATUS_TONE.new;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={status}
        onChange={(e) => onSelect(e.target.value)}
        data-testid={`client-status-${b.id}`}
        className={`appearance-none cursor-pointer border px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase outline-none ${tone}`}
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s} className="bg-white text-[#0A0A0A]">{STATUS_LABELS[s]}</option>
        ))}
      </select>

      {pending && (
        <span className="inline-flex items-center gap-1" data-testid={`client-status-pending-${b.id}`}>
          <input
            autoFocus
            type={pending === "scheduled" ? "date" : "number"}
            min="0"
            step="1"
            placeholder={pending === "scheduled" ? "" : "$ amount"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmPending(); if (e.key === "Escape") setPending(null); }}
            data-testid={`client-status-pending-input-${b.id}`}
            className="bg-[#FFFDF5] border-b border-[#0A0A0A] outline-none px-1 py-0.5 text-xs w-28"
          />
          <button onClick={confirmPending} data-testid={`client-status-confirm-${b.id}`} className="text-[10px] tracking-[0.2em] uppercase border border-[#0A0A0A] px-2 py-1 hover:bg-[#0A0A0A] hover:text-white">OK</button>
          <button onClick={() => setPending(null)} className="text-[10px] tracking-[0.2em] uppercase text-[#999] px-1 hover:text-[#0A0A0A]">✕</button>
        </span>
      )}

      {!pending && status === "scheduled" && (
        <DateChip value={b.scheduled_date} onSave={(v) => patch({ scheduled_date: v })} testId={`client-date-${b.id}`} />
      )}
      {!pending && status === "consulta" && (
        <DateChip value={b.consult_date} onSave={(v) => patch({ consult_date: v })} testId={`client-date-${b.id}`} placeholder="+ date" />
      )}
      {!pending && status === "archived_done" && (
        <>
          <DateChip value={b.scheduled_date} onSave={(v) => patch({ scheduled_date: v })} testId={`client-date-${b.id}`} placeholder="+ date" />
          <AmountChip value={b.amount_charged} onSave={(v) => patch({ amount_charged: v })} testId={`client-amount-${b.id}`} />
        </>
      )}
    </div>
  );
}

function DateChip({ value, onSave, testId, placeholder = "+ date" }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer group" title="Change date">
      <span data-testid={testId} className={`text-xs tracking-wider ${value ? "text-[#0A0A0A] font-medium" : "text-[#B5B5B5]"} group-hover:underline decoration-dashed`}>
        {value ? shortDate(value) : placeholder}
      </span>
      <input
        type="date"
        value={value || ""}
        onChange={(e) => onSave(e.target.value)}
        data-testid={`${testId}-input`}
        className="absolute inset-0 opacity-0 w-full cursor-pointer"
      />
    </label>
  );
}

function AmountChip({ value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const commit = () => {
    setEditing(false);
    const n = parseFloat(draft);
    if (isNaN(n) || n < 0 || n === value) return;
    onSave(n);
  };
  if (editing) {
    return (
      <input
        autoFocus type="number" min="0" step="1" value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`${testId}-input`}
        className="bg-[#FFFDF5] border-b border-[#0A0A0A] outline-none px-1 py-0.5 text-xs w-20"
      />
    );
  }
  return (
    <button onClick={() => { setDraft(value ?? ""); setEditing(true); }} data-testid={testId} className="text-xs font-medium hover:underline decoration-dashed" title="Change amount">
      ${Number(value || 0).toLocaleString("en-AU")}
    </button>
  );
}
